import React, { useEffect, useState } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import * as S from './styles';
import { GQL_GoogleClassroomCourses } from '../../../types/googleClassroom';
import { useQuery } from '@apollo/client';
import { gqlSchema } from '../../../gql/schema';

interface IClassInfos {
  classInfo?: GQL_GoogleClassroomCourses;
  loading: boolean;
}

// Function to determine the status style based on the state
const getStatusStyle = (state: string) => {
  switch (state) {
    case 'ACTIVE':
      return { color: '#3EBC89', text: 'Ongoing', fontWeight: 600 };
    case 'ARCHIVED':
      return { color: 'black', text: 'Archived' };
    case 'PROVISIONED':
      return { color: 'blue', text: 'Provisioned' };
    case 'DECLINED':
      return { color: 'red', text: 'Declined' };
    case 'SUSPENDED':
      return { color: 'orange', text: 'Suspended' };
    default:
      return { color: 'black', text: state };
  }
};

const ClassInfos: React.FC<IClassInfos> = ({ classInfo, loading }) => {
  const { user } = useAuth();
  const classTitle = classInfo ? (classInfo.room ? 'Class' : 'Class') : '';
  const [numberOfStudents, setNumberOfStudents] = useState<number | null>(null);

  // Fetch number of students
  const { data: studentsData, loading: studentsLoading } = useQuery<{ getCourseStudents: { numberOfStudents: number } }>(
    gqlSchema.GoogleClassroomSchema.queries.GOOGLEDASHBOARD.GetCourseStudents,
    {
      variables: { courseId: classInfo?.id },
      skip: !classInfo,
    }
  );

  useEffect(() => {
    if (studentsData) {
      console.log("Number of students:", studentsData.getCourseStudents.numberOfStudents);
      setNumberOfStudents(studentsData.getCourseStudents.numberOfStudents);
    }
  }, [studentsData]);

  // Get the status style for the class
  const statusStyle = classInfo ? getStatusStyle(classInfo.courseState) : { color: 'black', text: 'Not Available' };

  return (
    <S.ClassInfosContainer>
      <Row>
        {/* Class Name */}
        <Col span={8} lg={8} xl={8} xs={24}>
          <S.Title>{classTitle} Name</S.Title>
          {!loading ? (
            <p>{classInfo?.name}</p>
          ) : (
            <Skeleton.Input active size="small" />
          )}
        </Col>

        {/* Class Code */}
        <Col span={8} lg={8} xl={8} xs={24}>
          <S.Title>{classTitle} Code</S.Title>
          {!loading ? (
            <p>{classInfo?.enrollmentCode || 'Not provided'}</p>
          ) : (
            <Skeleton.Input active size="small" />
          )}
        </Col>

        {/* Course ID */}
        <Col span={8} lg={8} xl={8} xs={24}>
          <S.Title>{classTitle} ID</S.Title>
          {!loading ? (
            <p>{classInfo?.id || 'Not provided'}</p>
          ) : (
            <Skeleton.Input active size="small" />
          )}
        </Col>
      </Row>

      <Row>
        {/* Class Owner */}
        <Col span={8} lg={8} xl={8} xs={24}>
          <S.Title>{classTitle} Owner</S.Title>
          {!loading ? (
            <p>{user ? `${user.name} - ${user.email}` : '-'}</p>
          ) : (
            <Skeleton.Input active size="small" />
          )}
        </Col>

        {/* Class Status */}
        <Col span={8} lg={8} xl={8} xs={24}>
          <S.Title>{classTitle} Status</S.Title>
          {!loading ? (
            <p style={{ color: statusStyle.color, fontWeight: statusStyle.fontWeight || 'normal' }}>
              {statusStyle.text}
            </p>
          ) : (
            <Skeleton.Input active size="small" />
          )}
        </Col>

        {/* Class Students */}
        <Col span={8} lg={8} xl={8} xs={24}>
          <S.Title>{classTitle} Students</S.Title>
          {!loading && !studentsLoading ? (
            <p>{numberOfStudents !== null ? `${numberOfStudents} Enrolled Students` : '-'}</p>
          ) : (
            <Skeleton.Input active size="small" />
          )}
        </Col>
      </Row>
    </S.ClassInfosContainer>
  );
};

export default ClassInfos;
